// src/hooks/useSupabaseData.js
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

/**
 * useSupabaseData (fixed for today-first behavior)
 *
 * - Якщо tableName === 'schedules' і немає filter.date, то при автозавантаженні
 *   виконується запит саме для сьогоднішньої дати (щоб UI відразу показував сьогодні).
 * - Всі інші механізми (кешування за детермінованим ключем, пошук по кешу) збережені.
 * - Якщо ти хочеш отримати "всі" дані для schedules (без фільтра), викликай hook
 *   з autoFetch = false і виконай спеціальний запит ручно.
 */

export const useSupabaseData = (
  tableName,
  defaultValue = [],
  autoFetch = true,
  filter = null,
  includeDrafts = false
) => {
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const firstRunRef = useRef(true); // для визначення першого автозавантаження

  const CACHE_NAME = 'api-cache-v3';

  // ---------------- Helpers for normalization & cache keys ----------------
  const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

  const normalizeValue = (v) => {
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === 'string') {
      const dateMatch = v.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) return dateMatch[1];
      return v;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return v.map(normalizeValue).sort();
    if (isPlainObject(v)) {
      const keys = Object.keys(v).sort();
      const obj = {};
      keys.forEach(k => { obj[k] = normalizeValue(v[k]); });
      return obj;
    }
    return v;
  };

  const normalizeFilter = (f) => {
    if (!f) return null;
    return normalizeValue(f);
  };

  const makeCacheKey = (table, filterObj, includeDraftsFlag) => {
    const norm = normalizeFilter(filterObj);
    const payload = { filter: norm || null, includeDrafts: !!includeDraftsFlag };
    return `/__app_cache__/${encodeURIComponent(table)}/?q=${encodeURIComponent(JSON.stringify(payload))}`;
  };

  // ---------------- Cache I/O ----------------
  const writeJsonToCache = async (cacheName, keyUrl, jsonData) => {
    if (!('caches' in window)) return;
    try {
      const cache = await caches.open(cacheName);
      const resp = new Response(JSON.stringify(jsonData), {
        headers: { 'Content-Type': 'application/json' }
      });
      await cache.put(new Request(keyUrl), resp.clone());
      try {
        const meta = await caches.open(cacheName + '-meta');
        await meta.put(keyUrl, new Response(String(Date.now())));
      } catch (e) {
        // ignore meta errors
      }
      console.log(`[cache] wrote key=${keyUrl} items=${Array.isArray(jsonData) ? jsonData.length : '1'}`);
    } catch (e) {
      console.warn('[cache] writeJsonToCache failed', e);
    }
  };

  const readJsonFromCacheByKey = async (cacheName, keyUrl) => {
    if (!('caches' in window)) return null;
    try {
      const cache = await caches.open(cacheName);
      const resp = await cache.match(new Request(keyUrl));
      if (!resp) return null;
      try {
        const json = await resp.clone().json();
        return json;
      } catch {
        return null;
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[cache] readJsonFromCacheByKey failed', e);
      return null;
    }
  };

  const findMatchingCacheEntry = async (cacheName, table, filterObj) => {
    if (!('caches' in window)) return null;
    try {
      const cache = await caches.open(cacheName);
      const requests = await cache.keys();
      const candidates = requests.filter(r => r.url.includes(`/__app_cache__/${encodeURIComponent(table)}/`));
      if (!candidates.length) return null;

      if (table === 'schedules' && filterObj && (filterObj.date || filterObj.day)) {
        const desiredDate = normalizeValue(filterObj.date || filterObj.day);
        for (const req of candidates) {
          try {
            const resp = await cache.match(req);
            if (!resp) continue;
            const json = await resp.clone().json().catch(() => null);
            if (!json) continue;
            const arr = Array.isArray(json) ? json : (json.data || []);
            if (!arr || !arr.length) continue;
            const found = arr.some(item => {
              if (!item) return false;
              if (item.date) {
                const idate = String(item.date).slice(0,10);
                return idate === desiredDate;
              }
              return false;
            });
            if (found) {
              console.log(`[cache-search] found matching cached entry ${req.url} for date=${desiredDate}`);
              return json;
            }
          } catch (e) {
            // ignore
          }
        }
      }
      return null;
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[cache] findMatchingCacheEntry failed', e);
      return null;
    }
  };

  // ---------------- Utility: today's date string ----------------
  const todayISO = () => (new Date()).toISOString().slice(0,10);

  // ---------------- Main fetch function ----------------
  const fetchData = async () => {
    setLoading(true);

    // If it's the first run and the table is schedules and no date filter provided,
    // we default to fetching data for today so that initial view shows today's schedule.
    let effectiveFilter = filter;
    if (tableName === 'schedules' && firstRunRef.current && (!filter || !filter.date)) {
      effectiveFilter = { ...(filter || {}), date: todayISO() };
      console.log('[useSupabaseData] first run for schedules -> defaulting to today:', effectiveFilter);
    }

    const cacheKey = makeCacheKey(tableName, effectiveFilter, includeDrafts);

    // If offline -> try exact cached key, then matched entry
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const exact = await readJsonFromCacheByKey(CACHE_NAME, cacheKey);
      if (exact) {
        setData(exact);
        toast({ title: `Дані з кешу для "${tableName}"`, description: 'Показано останню збережену версію (offline).' });
        setLoading(false);
        return;
      }
      const matched = await findMatchingCacheEntry(CACHE_NAME, tableName, effectiveFilter);
      if (matched) {
        setData(matched);
        toast({ title: `Дані з кешу (по збігу) для "${tableName}"`, description: 'Показано відповідну кешовану дату.' });
        setLoading(false);
        return;
      }
      toast({ title: `Немає інтернету та кешу`, description: `Неможливо завантажити "${tableName}" для обраних параметрів.`, variant: 'destructive' });
      setLoading(false);
      return;
    }

    // ONLINE path: perform supabase query (with effectiveFilter)
    try {
      let query = supabase.from(tableName).select('*');

      if (tableName === 'schedules') {
        if (!includeDrafts) query = query.eq('published', true);
        query = query.order('date', { ascending: true }).order('time_slot', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: true });
      }

      if (effectiveFilter && typeof effectiveFilter === 'object') {
        for (const [key, value] of Object.entries(effectiveFilter)) {
          query = query.eq(key, value);
        }
      }

      const { data: fetchedData, error } = await query;

      if (error) {
        if (process.env.NODE_ENV === 'development') console.error(`Supabase fetchData error from ${tableName}:`, error);
        const exact = await readJsonFromCacheByKey(CACHE_NAME, cacheKey);
        if (exact) {
          setData(exact);
          toast({ title: `Помилка мережі — показано кеш для "${tableName}"`, description: 'Показано останню збережену версію.' });
        } else {
          const matched = await findMatchingCacheEntry(CACHE_NAME, tableName, effectiveFilter);
          if (matched) {
            setData(matched);
            toast({ title: `Помилка — показано кеш по збігу для "${tableName}"`, description: 'Показано відповідну кешовану дату.' });
          } else {
            toast({ title: `Помилка завантаження з "${tableName}"`, description: error.message || String(error), variant: 'destructive' });
          }
        }
        setLoading(false);
        firstRunRef.current = false;
        return;
      }

      // SUCCESS: update state and write to cache under the *exact* key
      setData(fetchedData || defaultValue);

      // avoid writing empty arrays accidentally (only write if response has useful content)
      if (Array.isArray(fetchedData) && fetchedData.length > 0) {
        writeJsonToCache(CACHE_NAME, cacheKey, fetchedData || defaultValue).catch(()=>{});
      } else if (!Array.isArray(fetchedData) && fetchedData) {
        writeJsonToCache(CACHE_NAME, cacheKey, fetchedData).catch(()=>{});
      } else {
        // if empty result, we still may want to cache explicitly only if there's a reason;
        // skip to avoid overwriting more specific cache with an empty "all dates" response
        console.log('[useSupabaseData] fetchedData empty — skipping cache write for key', cacheKey);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.warn(`Supabase fetch exception for ${tableName}:`, error);
      const exact = await readJsonFromCacheByKey(CACHE_NAME, cacheKey);
      if (exact) {
        setData(exact);
        toast({ title: `Офлайн: показано кеш для "${tableName}"`, description: 'Мережа недоступна, показано останні збережені дані.' });
      } else {
        const matched = await findMatchingCacheEntry(CACHE_NAME, tableName, effectiveFilter);
        if (matched) {
          setData(matched);
          toast({ title: `Офлайн: показано кеш по збігу для "${tableName}"`, description: 'Показано відповідну кешовану дату.' });
        } else {
          toast({ title: `Помилка завантаження з "${tableName}"`, description: error?.message || 'Немає з\'єднання або сталася помилка.', variant: 'destructive' });
        }
      }
    } finally {
      setLoading(false);
      firstRunRef.current = false;
    }
  };

  // useEffect - залежності: спростили filter порівняння (normalize + JSON)
  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, autoFetch, JSON.stringify(normalizeFilter(filter)), includeDrafts]);

  // CRUD ops (скопіюй зі свого існуючого коду; вони не змінювалися)
  const addData = async (newData) => {
    try {
      const { data: insertedData, error } = await supabase
        .from(tableName)
        .insert(newData)
        .select()
        .single();

      if (error) throw error;

      setData(prev => [...prev, insertedData]);
      toast({ title: "✅ Успішно додано!", description: `Запис додано до "${tableName}".` });
      return insertedData;
    } catch (error) {
      toast({ title: `❌ Помилка додавання у "${tableName}"`, description: error.message, variant: 'destructive' });
      return null;
    }
  };

  const updateData = async (id, updatedData) => {
    try {
      const { data: updated, error } = await supabase
        .from(tableName)
        .update(updatedData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setData(prev => prev.map(item => item.id === id ? updated : item));
      toast({ title: "✅ Успішно оновлено!", description: `Запис у "${tableName}" оновлено.` });
      return updated;
    } catch (error) {
      toast({ title: `❌ Помилка оновлення у "${tableName}"`, description: error.message, variant: 'destructive' });
      return null;
    }
  };

  const deleteData = async (id) => {
    try {
      const { error } = await supabase.from(tableName).delete().eq('id', id);
      if (error) throw error;

      setData(prev => prev.filter(item => item.id !== id));
      toast({ title: "🗑️ Видалено!", description: `Запис видалено з "${tableName}".` });
      return true;
    } catch (error) {
      toast({ title: `❌ Помилка видалення з "${tableName}"`, description: error.message, variant: 'destructive' });
      return false;
    }
  };

  const bulkAdd = async (items) => {
    if (!items || items.length === 0) {
      toast({ title: "ℹ️ Порожній список", description: "Немає даних для масового додавання." });
      return [];
    }
    try {
      const { data: insertedData, error } = await supabase.from(tableName).insert(items).select();
      if (error) throw error;
      if (insertedData?.length > 0) {
        setData(prev => [...prev, ...insertedData]);
        toast({ title: "✅ Масове додавання", description: `${insertedData.length} запис(ів) додано у "${tableName}".` });
      }
      return insertedData || [];
    } catch (error) {
      toast({ title: `❌ Помилка масового додавання у "${tableName}"`, description: error.message, variant: 'destructive' });
      return null;
    }
  };

  return {
    data,
    loading,
    addData,
    updateData,
    deleteData,
    bulkAdd,
    refreshData: fetchData,
  };
};
